/**
 * CopyableValue - Core component for displaying copyable values with tooltips
 *
 * A flexible, reusable component that displays values with click-to-copy functionality,
 * hover tooltips, and visual feedback. Supports multiple variants for different use cases.
 *
 * @example
 * ```tsx
 * <CopyableValue
 *   value="abc123"
 *   variant="id"
 *   label="transaction id"
 *   onCopy={(value) => console.log('Copied:', value)}
 * />
 * ```
 */

import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { CopyableValueProps, TooltipPosition } from './types';
import {
  CopyableValueWrapper,
  CopyableValueTrigger,
  EmptyStateBadge,
  PortalTooltipBubble,
  TooltipRow,
  TooltipStack,
  TooltipLabel,
  TooltipValue,
  TooltipHint,
  TooltipDivider,
  TooltipMuted,
  MemberPreview,
  MemberAvatar,
  MemberInfo,
  MemberName,
  MemberEmail,
} from './styles';
import {
  useTooltipPosition,
  copyToClipboard,
  getIconForVariant,
  getCopyIndicatorIcon,
  getCheckmarkIcon,
} from './utils';

/**
 * Portal Tooltip Component
 * Renders tooltip content in a portal for proper z-index layering
 */
const PortalTooltip: React.FC<{
  visible: boolean;
  position: TooltipPosition;
  variant?: 'default' | 'success';
  maxWidth?: number;
  children: React.ReactNode;
}> = ({ visible, position, variant = 'default', maxWidth, children }) => {
  if (!visible) return null;

  return ReactDOM.createPortal(
    <PortalTooltipBubble
      $visible={visible}
      $position={position}
      $variant={variant}
      $maxWidth={maxWidth}
    >
      {children}
    </PortalTooltipBubble>,
    document.body
  );
};

/**
 * CopyableValue Component
 */
export const CopyableValue: React.FC<CopyableValueProps> = ({
  value,
  variant = 'generic',
  icon,
  emptyIcon,
  emptyText = '—',
  label,
  tooltipContent,
  memberData,
  disabled = false,
  copySuccessMessage = 'copied!',
  onCopy,
  size,
  className,
  style,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Determine tooltip dimensions based on variant
  const hasMemberPreview = variant === 'member' && !!(memberData?.fullName || memberData?.email);
  const tooltipWidth = hasMemberPreview ? 240 : variant === 'summary' ? 300 : 220;
  const tooltipHeight = hasMemberPreview ? 100 : variant === 'summary' ? 100 : 60;

  // Calculate tooltip positions
  const mainTooltipPosition = useTooltipPosition(
    triggerRef,
    isHovered && !copied,
    tooltipWidth,
    tooltipHeight,
    10,  // gap above - reduced to 10px to bring summary way down matching amount column
    15   // gap below
  );
  const successTooltipPosition = useTooltipPosition(triggerRef, copied, 100, 40, 10, 15);  // gap above reduced to 10px

  // Handle copy action
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !value) return;

    const success = await copyToClipboard(value);
    if (success) {
      setCopied(true);
      onCopy?.(value);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsHovered(false);
    setCopied(false);
  };

  // Render icon based on variant or custom icon
  const renderIcon = () => {
    if (icon) return icon;
    if (emptyIcon && !value) return emptyIcon;
    return getIconForVariant(variant);
  };

  // If value is empty and disabled, show empty state badge
  if (!value && !disabled) {
    return <EmptyStateBadge $size={size}>{emptyText}</EmptyStateBadge>;
  }

  // Determine tooltip title
  const tooltipTitle = disabled && !value
    ? `no ${label || variant} available`
    : `click to copy ${label || variant}`;

  return (
    <CopyableValueWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={className}
      style={style}
    >
      <CopyableValueTrigger
        ref={triggerRef}
        onClick={handleCopy}
        $copied={copied}
        $variant={variant}
        $disabled={disabled}
        title={tooltipTitle}
      >
        {renderIcon()}
        {value && !disabled && (
          <span className="copy-indicator">
            {getCopyIndicatorIcon()}
          </span>
        )}
      </CopyableValueTrigger>

      {/* Main tooltip */}
      <PortalTooltip
        visible={isHovered && !copied}
        position={mainTooltipPosition}
        maxWidth={tooltipWidth}
      >
        {tooltipContent || (
          <TooltipStack>
            <TooltipRow $justify="between">
              {label && variant !== 'member' && (
                <>
                  <TooltipLabel>{label}</TooltipLabel>
                  {value && !disabled && (
                    <TooltipHint>
                      {getCopyIndicatorIcon()}
                      click
                    </TooltipHint>
                  )}
                </>
              )}
              {variant === 'member' && (
                <>
                  <TooltipRow>
                    <TooltipLabel>{label || 'member'}</TooltipLabel>
                    <TooltipValue $mono>{value}</TooltipValue>
                  </TooltipRow>
                  {value && !disabled && (
                    <TooltipHint>
                      {getCopyIndicatorIcon()}
                      click
                    </TooltipHint>
                  )}
                </>
              )}
            </TooltipRow>

            {/* Render value for non-member variants */}
            {variant !== 'member' && (
              <>
                {value ? (
                  <TooltipValue $mono={variant === 'id'}>{value}</TooltipValue>
                ) : (
                  <TooltipMuted>no {label || variant} available</TooltipMuted>
                )}
              </>
            )}

            {/* Render member preview if available */}
            {hasMemberPreview && (
              <>
                <TooltipDivider />
                <MemberPreview>
                  <MemberAvatar>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </MemberAvatar>
                  <MemberInfo>
                    {memberData?.fullName && <MemberName>{memberData.fullName}</MemberName>}
                    {memberData?.email && <MemberEmail>{memberData.email}</MemberEmail>}
                  </MemberInfo>
                </MemberPreview>
              </>
            )}
          </TooltipStack>
        )}
      </PortalTooltip>

      {/* Success tooltip */}
      <PortalTooltip
        visible={copied}
        position={successTooltipPosition}
        variant="success"
        maxWidth={100}
      >
        <TooltipRow $justify="center">
          <span style={{ width: 12, height: 12 }}>
            {getCheckmarkIcon()}
          </span>
          {copySuccessMessage}
        </TooltipRow>
      </PortalTooltip>
    </CopyableValueWrapper>
  );
};

export default CopyableValue;
